import type { ColumnDef, Table as TanTable, Row } from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import { DataTableColumnHeader } from '../DataTable/DataTableColumnHeader';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DataTableRowActions } from '../DataTable/DataTableRowActions';
import type { RowAction } from '../DataTable/DataTableRowActions';
import { ACTIONS_COLUMN_WIDTH, CHECKBOX_CLASSES, WRAP_ANYWHERE_CLASS } from '../DataTable/constants';
import { PRODUCT_COLUMN_WIDTHS } from '../DataTable/columnWidths';
import {
  EmptySkuIcon,
  EditIcon,
  DuplicateIcon,
  DeactivateIcon,
  CheckCircleIcon,
  PublishIcon,
} from '../icons';
import { ClampedTextCell } from '../DataTable/ClampedTextCell';
import { EquipmentCell } from './EquipmentCell';
import { NameWithDietIcons } from './NameWithDietIcons';
import { ProductStatusPill } from './Sections/ProductStatusPill';
import { playTickSound } from '../../utils/sounds';
import { formatCurrency, formatDateRange, formatPercent, formatTagParts } from '../../utils/formatters';
import { DEFAULT_FORM_LABELS } from './types';
import type { Product, ProductPublishedStatus } from './types';

// Figma spec: node 8486-82447 — widths centralized in DataTable/columnWidths.ts
const colWidths = PRODUCT_COLUMN_WIDTHS;

const layout = {
  cellGap: 18,
  textGap: 3,
  thumbnail: 48,
} as const;

function formatMacro(value: number | null | undefined, unit?: string): string {
  if (value == null) return '—';
  return unit ? `${value}${unit}` : `${value}`;
}

const textCell = 'text-sm leading-[1.5] text-off-black';
const tabularCell = 'text-sm leading-[1.5]';
const badgeBase = 'rounded-xs px-2 py-[3px] text-xs font-normal leading-[1.25] whitespace-nowrap';

function SelectAllHeader({ table }: { table: TanTable<Product> }) {
  const allSelected = table.getIsAllPageRowsSelected();
  return (
    <Checkbox
      checked={allSelected || (table.getIsSomePageRowsSelected() && 'indeterminate')}
      onCheckedChange={(value) => {
        table.toggleAllPageRowsSelected(!!value);
        playTickSound(!!value);
      }}
      onClick={(e) => e.stopPropagation()}
      aria-label="Select all"
      className={CHECKBOX_CLASSES}
    />
  );
}

function SelectRowCell({ row }: { row: Row<Product> }) {
  const selected = row.getIsSelected();
  return (
    <Checkbox
      checked={selected}
      onCheckedChange={(value) => {
        row.toggleSelected(!!value);
        playTickSound(!!value);
      }}
      onClick={(e) => e.stopPropagation()}
      aria-label="Select row"
      className={CHECKBOX_CLASSES}
    />
  );
}

/**
 * The catalogue column is 147px wide, which fits exactly one badge. A product
 * in two or more catalogues used to render them all into a horizontally
 * scrolling div with the scrollbar hidden, so the second badge onwards was
 * clipped with nothing to say it existed — a three-catalogue product hid 112
 * of its 223px of content. Show the first and count the rest, with the full
 * list on hover. A single catalogue, which is every product in production
 * today, renders exactly as before and pays for no tooltip.
 */
function CatalogueCell({ catalogues }: { catalogues: string[] }) {
  if (catalogues.length === 0) return <span className={textCell}>—</span>;

  const [first, ...rest] = catalogues;
  const badgeClasses = cn(badgeBase, 'border-off-black text-off-black');
  const firstBadge = (
    <Badge variant="outline" className={cn(badgeClasses, 'min-w-0 shrink truncate')} title={first}>
      {first}
    </Badge>
  );

  if (rest.length === 0) return <div className="flex gap-2">{firstBadge}</div>;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex min-w-0 gap-2" aria-label={catalogues.join(', ')}>
            {firstBadge}
            {/* Never truncated: the count is the whole point of the cell. */}
            <Badge variant="outline" className={cn(badgeClasses, 'shrink-0')}>
              +{rest.length}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start">
          {catalogues.join(', ')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const noop = () => {};

export interface ProductRowActionCallbacks {
  onDuplicate?: (product: Product) => void;
  onActivate?: (product: Product) => void;
  onDeactivate?: (product: Product) => void;
  onPublish?: (product: Product) => void;
  currentStatus?: ProductPublishedStatus;
  /** RBAC: when false, the selection checkbox + row action menu columns are
   * omitted entirely (every row action is a write). Defaults to false. */
  canWrite?: boolean;
}

function getStateAction(product: Product, callbacks: ProductRowActionCallbacks): RowAction {
  const { currentStatus, onPublish, onActivate, onDeactivate } = callbacks;
  if (currentStatus === 'unpublished') {
    return {
      label: 'Publish',
      icon: <PublishIcon className="w-3.5 h-3.5" />,
      onClick: onPublish ? () => onPublish(product) : noop,
    };
  }
  if (product.active_status === 'active') {
    return {
      label: 'Deactivate',
      icon: <DeactivateIcon className="size-5" />,
      onClick: onDeactivate ? () => onDeactivate(product) : noop,
    };
  }
  return {
    label: 'Activate',
    icon: <CheckCircleIcon className="w-3.5 h-3.5" />,
    onClick: onActivate ? () => onActivate(product) : noop,
  };
}

function getProductRowActions(product: Product, callbacks: ProductRowActionCallbacks = {}): RowAction[] {
  const { onDuplicate } = callbacks;
  return [
    {
      label: 'Edit',
      icon: <EditIcon className="size-5" />,
      onClick: () => { window.location.href = `/products/${product.id}/edit`; },
    },
    {
      label: 'Duplicate',
      icon: <DuplicateIcon className="w-3.5 h-3.5" />,
      onClick: onDuplicate ? () => onDuplicate(product) : noop,
    },
    getStateAction(product, callbacks),
  ];
}

function createBaseColumns(
  t: (key: string) => string,
  callbacks: ProductRowActionCallbacks = {},
): ColumnDef<Product>[] {
  const columns: ColumnDef<Product>[] = [
    {
      id: 'select',
      enableSorting: false,
      size: colWidths.select,
      meta: { frozen: true },
      header: ({ table }) => <SelectAllHeader table={table} />,
      cell: ({ row }) => <SelectRowCell row={row} />,
    },
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.product')} />,
      size: colWidths.product,
      meta: { frozen: true },
      cell: ({ row }) => (
        <div className="flex items-center" style={{ gap: layout.cellGap }}>
          <div
            className="flex shrink-0 items-center justify-center rounded-input bg-dinnerplate-white overflow-hidden"
            style={{ width: layout.thumbnail, height: layout.thumbnail }}
          >
            {row.original.images && row.original.images.length > 0 ? (
              <img src={row.original.images[0].url} alt="" width={layout.thumbnail} height={layout.thumbnail} className="h-full w-full object-cover" />
            ) : (
              <EmptySkuIcon className="text-grey" />
            )}
          </div>
          <div className="flex flex-col" style={{ gap: layout.textGap }}>
            <NameWithDietIcons
              name={row.original.name}
              dietTags={row.original.diet_tags}
              chefRecommended={row.original.chef_recommended}
              className={`text-sm font-semibold leading-[1.5] text-off-black ${WRAP_ANYWHERE_CLASS}`}
            />
            {/* The format shares the byline's line rather than taking one of
                its own. 188 published names appear on two or more rows, so the
                format is what tells them apart — but the other ~450 rows would
                otherwise pay a third line for a badge that distinguishes them
                from nothing. Here the stack stays under the 48px thumbnail, so
                the thumbnail keeps setting the row height. It is unconditional:
                a badge that came and went as you filtered would make a row's
                identity feel unstable, and it is the one thing separating the
                identical-looking name pairs. */}
            <div className="flex min-w-0 items-center" style={{ gap: layout.textGap * 2 }}>
              <Badge variant="tag" className="shrink-0">
                {DEFAULT_FORM_LABELS[row.original.form] ?? row.original.form}
              </Badge>
              {row.original.byline && (
                <span className="min-w-0 flex-1 text-xs leading-[1.25] text-grey line-clamp-1">
                  {row.original.byline}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.category')} />,
      size: colWidths.data,
      cell: ({ row }) =>
        row.original.category ? (
          <Badge
            variant="static"
            className={cn(badgeBase, 'justify-center w-full min-w-0 border-0 bg-dinnerplate-white text-grey')}
            title={row.original.category}
          >
            <span className="truncate">{row.original.category}</span>
          </Badge>
        ) : (
          <span className={textCell}>—</span>
        ),
    },
    {
      id: 'catalogue',
      header: t('table.columns.catalogue'),
      size: colWidths.data,
      enableSorting: false,
      cell: ({ row }) => <CatalogueCell catalogues={row.original.catalogues ?? []} />,
    },
    {
      id: 'active_period',
      header: t('table.columns.active_period'),
      size: colWidths.activePeriod,
      enableSorting: false,
      cell: ({ row }) => (
        <span className={cn(textCell, 'whitespace-nowrap')}>
          {formatDateRange(row.original.active_period_start, row.original.active_period_end)}
        </span>
      ),
    },
    {
      id: 'status',
      header: t('table.columns.status'),
      size: colWidths.status,
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.status === 'draft') return <span className={textCell}>—</span>;
        const isActive = row.original.active_status === 'active';
        return <ProductStatusPill status={isActive ? 'active' : 'inactive'} />;
      },
    },
    {
      accessorKey: 'price_excluding_gst',
      header: t('table.columns.before_gst'),
      size: colWidths.data,
      enableSorting: false,
      cell: ({ row }) => <span className={tabularCell}>{formatCurrency(row.original.price_excluding_gst)}</span>,
    },
    {
      accessorKey: 'price_including_gst',
      header: t('table.columns.after_gst'),
      size: colWidths.data,
      enableSorting: false,
      cell: ({ row }) => <span className={tabularCell}>{formatCurrency(row.original.price_including_gst)}</span>,
    },
    {
      accessorKey: 'food_cost',
      header: t('table.columns.food_cost'),
      size: colWidths.data,
      enableSorting: false,
      cell: ({ row }) => <span className={tabularCell}>{formatCurrency(row.original.food_cost)}</span>,
    },
    {
      accessorKey: 'food_cost_percent',
      header: t('table.columns.food_cost_percent'),
      size: colWidths.data,
      enableSorting: false,
      cell: ({ row }) => <span className={tabularCell}>{formatPercent(row.original.food_cost_percent)}</span>,
    },
    {
      id: 'tags',
      header: t('table.columns.tags'),
      size: colWidths.wide,
      enableSorting: false,
      cell: ({ row }) => {
        const { diet_tags, chef_recommended, nutrition_tags, allergen_tags } = row.original;
        const parts = formatTagParts(diet_tags, chef_recommended, nutrition_tags, allergen_tags, t('common.fields.chefs_pick'));
        return <span className={textCell}>{parts.length > 0 ? parts.join(', ') : '—'}</span>;
      },
    },
    {
      id: 'ingredients',
      header: t('table.columns.ingredients'),
      size: colWidths.wide,
      enableSorting: false,
      cell: ({ row }) => <ClampedTextCell text={row.original.ingredients} className={textCell} />,
    },
    {
      accessorKey: 'serving_temperature',
      header: t('table.columns.temperature'),
      size: colWidths.data,
      enableSorting: false,
      cell: ({ row }) => <span className={textCell}>{row.original.serving_temperature ?? '—'}</span>,
    },
    {
      id: 'key_serving_info',
      header: t('table.columns.key_serving_info'),
      size: colWidths.wide,
      enableSorting: false,
      cell: ({ row }) => <ClampedTextCell text={row.original.key_serving_info} className={textCell} />,
    },
    {
      id: 'story',
      header: t('table.columns.story'),
      size: colWidths.wide,
      enableSorting: false,
      cell: ({ row }) => <ClampedTextCell text={row.original.story} className={textCell} />,
    },
    {
      id: 'equipment',
      header: t('table.columns.equipment'),
      size: colWidths.equipment,
      enableSorting: false,
      cell: ({ row }) => (
        <EquipmentCell
          inventory_items={row.original.inventory_items}
          container_items={row.original.container_items}
        />
      ),
    },
    {
      id: 'stocks',
      header: t('table.columns.stocks'),
      size: colWidths.stocks,
      enableSorting: false,
      cell: ({ row }) => (
        <span className={cn(textCell, 'whitespace-nowrap')}>{formatDateRange(row.original.stocks_start, row.original.stocks_end)}</span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: ACTIONS_COLUMN_WIDTH,
      cell: ({ row }) => <DataTableRowActions actions={getProductRowActions(row.original, callbacks)} />,
    },
  ];

  // RBAC: read-only users get no selection checkbox (bulk actions are writes)
  // and no row action menu (Edit / Duplicate / Publish / Activate / Deactivate
  // are all writes). Drop both columns entirely. Fail closed.
  if (!callbacks.canWrite) {
    return columns.filter((c) => c.id !== 'select' && c.id !== 'actions');
  }
  return columns;
}

const servingSizeColumn = (t: (key: string) => string): ColumnDef<Product> => ({
  id: 'serving_size',
  header: t('table.columns.serving_size'),
  size: colWidths.data,
  enableSorting: false,
  cell: ({ row }) => <span className={textCell}>{row.original.serving_size ?? '—'}</span>,
});

const macroColumns = (t: (key: string) => string): ColumnDef<Product>[] => [
  {
    id: 'calories',
    header: t('table.columns.calories'),
    size: colWidths.data,
    enableSorting: false,
    cell: ({ row }) => <span className={tabularCell}>{formatMacro(row.original.macros?.calories)}</span>,
  },
  {
    id: 'protein',
    header: t('table.columns.protein'),
    size: colWidths.data,
    enableSorting: false,
    cell: ({ row }) => <span className={tabularCell}>{formatMacro(row.original.macros?.protein, 'g')}</span>,
  },
  {
    id: 'fat',
    header: t('table.columns.fat'),
    size: colWidths.data,
    enableSorting: false,
    cell: ({ row }) => <span className={tabularCell}>{formatMacro(row.original.macros?.fats, 'g')}</span>,
  },
  {
    id: 'carbs',
    header: t('table.columns.carbs'),
    size: colWidths.data,
    enableSorting: false,
    cell: ({ row }) => <span className={tabularCell}>{formatMacro(row.original.macros?.carbohydrates, 'g')}</span>,
  },
];

function insertAfter(
  base: ColumnDef<Product>[],
  afterId: string,
  columns: ColumnDef<Product>[],
): ColumnDef<Product>[] {
  const idx = base.findIndex((c) => c.id === afterId);
  return [...base.slice(0, idx + 1), ...columns, ...base.slice(idx + 1)];
}

export function createBuffetColumns(
  t: (key: string) => string,
  callbacks: ProductRowActionCallbacks = {},
): ColumnDef<Product>[] {
  const base = createBaseColumns(t, callbacks);
  return insertAfter(base, 'ingredients', [servingSizeColumn(t)]);
}

export function createMealBoxColumns(
  t: (key: string) => string,
  callbacks: ProductRowActionCallbacks = {},
): ColumnDef<Product>[] {
  const base = createBaseColumns(t, callbacks);
  return insertAfter(base, 'ingredients', macroColumns(t));
}

// The union set, used when the format selection mixes the two families or is
// empty (every format). Serving size is filled on most buffet-family products
// and macros on most box-family ones, so showing both is strictly more
// informative than picking one family's columns for a mixed list.
export function createAllFormatColumns(
  t: (key: string) => string,
  callbacks: ProductRowActionCallbacks = {},
): ColumnDef<Product>[] {
  const base = createBaseColumns(t, callbacks);
  return insertAfter(base, 'ingredients', [servingSizeColumn(t), ...macroColumns(t)]);
}
